import Icon from '@atoms/Icon/Icon';
import LoadingOverlay from '@atoms/LoadingOverlay/LoadingOverlay';
import { IAddToCartTray } from '@models/IAddToCartTray';
import { ILink } from '@models/ILink';
import { IMessage } from '@models/IMessage';
import { IPricingData } from '@models/IPricingData';
import { IProductCommercialText } from '@models/IProductCommercialText';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { IProductVariant } from '@models/IProductVariant';
import { ISharePanel } from '@models/ISharePanel';
import AddToCartErrorEnum from '@models/enums/AddToCartErrorEnum';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import AddToCartTray from '@molecules/AddToCartTray/AddToCartTray';
import InfoModal from '@molecules/InfoModal/InfoModal';
import ProductDetailsCta from '@molecules/ProductDetailsCta/ProductDetailsCta';
import ProductDetailsInformation from '@molecules/ProductDetailsInformation/ProductDetailsInformation';
import ProductDetailsOptions from '@molecules/ProductDetailsOptions/ProductDetailsOptions';
import ProductDetailsStockStatus from '@molecules/ProductDetailsStockStatus/ProductDetailsStockStatus';
import ProductDetailsTitle from '@molecules/ProductDetailsTitle/ProductDetailsTitle';
import { addItemToCart } from '@services/cart';
import useCartSizeStore from '@state/CartSizeStore';
import updateDataLayer from '@utils/helpers/gtm';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { useMemo, useState } from 'react';

interface IAddToCartModal {
  readonly title: string;
  readonly description?: string;
  readonly type: InfoTypeEnum;
  readonly ctaLink?: ILink;
}

interface Props {
  readonly addToCartAddedLabel: string;
  readonly addToCartErrorLabel: string;
  readonly backOrderText: IMessage;
  readonly basketEndpoint: string;
  readonly basketLink: ILink;
  readonly buyNowLabel?: string;
  readonly buyNowUrl?: string;
  readonly code: string;
  readonly colours: IProductVariant[];
  readonly coloursLabel: string;
  readonly commercialText: IProductCommercialText;
  readonly copyTaxLabel: string;
  readonly dataTestId?: string;
  readonly dealerFindLink: ILink;
  readonly deliveryInformation: IMessage;
  readonly disableFindDealerCta: boolean;
  readonly ecoTaxLabel: string;
  readonly hidePriceFromSavePill: boolean;
  readonly isRefurbished: boolean;
  readonly maxBasketQuantityText: IMessage;
  readonly maxBasketValueText: IMessage;
  readonly maxProductQuantityText: IMessage;
  readonly mixedCartText: IMessage;
  readonly name: string;
  readonly notifyMeContactFormEndpoint: string;
  readonly preOrderText: IMessage;
  readonly pricingData: IPricingData;
  readonly primarySkuCode: string;
  readonly productsSubTitle: string;
  readonly productsTitle: string;
  readonly refurbishedIcon: string;
  readonly refurbishedInformation: IMessage;
  readonly refurbishedLabel: string;
  readonly regionalTaxLabel: string;
  readonly savePriceLabel: string;
  readonly shareOptions: ISharePanel;
  readonly skuCode: string;
  readonly skuLabel: string;
  readonly statusLabels: IProductListingStatusLabels;
  readonly usbCharger?: 'included' | 'not_included';
  readonly usbChargerLabel?: string;
  readonly variations: IProductVariant[];
  readonly variationsLabel: string;
}

const ProductDetailsSidePanel = ({
  addToCartAddedLabel,
  addToCartErrorLabel,
  backOrderText,
  basketEndpoint,
  basketLink,
  buyNowLabel,
  buyNowUrl,
  code,
  colours,
  coloursLabel,
  commercialText,
  copyTaxLabel,
  dataTestId = 'Product-Details-Side-Panel',
  dealerFindLink,
  deliveryInformation,
  disableFindDealerCta,
  ecoTaxLabel,
  hidePriceFromSavePill,
  isRefurbished,
  maxBasketQuantityText,
  maxBasketValueText,
  maxProductQuantityText,
  mixedCartText,
  name,
  notifyMeContactFormEndpoint,
  preOrderText,
  pricingData,
  primarySkuCode,
  productsSubTitle,
  productsTitle,
  refurbishedIcon,
  refurbishedInformation,
  refurbishedLabel,
  regionalTaxLabel,
  savePriceLabel,
  shareOptions,
  skuCode,
  skuLabel,
  statusLabels,
  usbCharger,
  usbChargerLabel,
  variations,
  variationsLabel,
}: Props) => {
  const {
    status,
    amountSaved,
    formattedAmountSaved,
    price,
    formattedPrice,
    formattedCalculatedPrice,
    ecoTax,
    formattedEcoTax,
    copyTax,
    formattedCopyTax,
    isCommerceEnabled,
  } = pricingData || {};

  const limitedDisplay = useMemo(
    () =>
      status === ProductStockStatusEnum.DoNotShow ||
      status === ProductStockStatusEnum.BtoBOnly ||
      status === undefined ||
      status === null ||
      !status,
    [status]
  );

  const { setCartSize, cartSizeName } = useCartSizeStore((state) => ({
    cartSizeName: state.cartSizeName,
    setCartSize: state.setCartSize,
  }));

  const [addToCartModalContent, setAddToCartModalContent] =
    useState<IAddToCartModal>({
      title: '',
      description: '',
      type: InfoTypeEnum.Information,
    });
  const [isAddToCartModalOpen, setIsAddToCartModalOpen] = useState(false);
  const [isAddToCartTrayOpen, setIsAddToCartTrayOpen] = useState(false);
  const [addToCartTrayPayload, setAddToCartTrayPayload] =
    useState<IAddToCartTray>();
  const [isLoading, setIsLoading] = useState(false);

  const handleAddToCartModal = (content: IAddToCartModal) => {
    setAddToCartModalContent(content);
    setIsAddToCartModalOpen(true);
  };

  const addToCartErrorHandler = (error: AxiosError) => {
    const errorCode = error.response?.data?.errorCode;

    if (errorCode === AddToCartErrorEnum.mixedCart) {
      handleAddToCartModal({
        title: mixedCartText.title,
        description: mixedCartText.text,
        type: InfoTypeEnum.Error,
      });

      return;
    }

    if (errorCode === AddToCartErrorEnum.maxProductQuantity) {
      handleAddToCartModal({
        title: maxProductQuantityText.title,
        description: maxProductQuantityText.text,
        type: InfoTypeEnum.Error,
        ...(isCommerceEnabled ? { ctaLink: basketLink } : {}),
      });

      return;
    }

    if (errorCode === AddToCartErrorEnum.maxBasketQuantity) {
      handleAddToCartModal({
        title: maxBasketQuantityText.title,
        description: maxBasketQuantityText.text,
        type: InfoTypeEnum.Error,
        ctaLink: basketLink,
      });

      return;
    }

    if (errorCode === AddToCartErrorEnum.maxBasketValue) {
      handleAddToCartModal({
        title: maxBasketValueText.title,
        description: maxBasketValueText.text,
        type: InfoTypeEnum.Error,
        ctaLink: basketLink,
      });

      return;
    }

    handleAddToCartModal({
      title: addToCartErrorLabel,
      type: InfoTypeEnum.Error,
    });
  };

  const addToCartHandler = async () => {
    setIsLoading(true);
    try {
      const addToCartTrayPayloadResponse = await addItemToCart(basketEndpoint, {
        code: isCommerceEnabled ? code : skuCode,
        quantity: 1,
      });

      if (isCommerceEnabled) {
        setAddToCartTrayPayload(addToCartTrayPayloadResponse);
        setIsAddToCartTrayOpen(true);
      } else {
        handleAddToCartModal({
          title: addToCartAddedLabel,
          type: InfoTypeEnum.Success,
        });
      }

      setIsLoading(false);
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);

      if (productData) {
        const updatedProductData = {
          ...productData,
          ...(isCommerceEnabled
            ? {
                quantity:
                  addToCartTrayPayloadResponse?.addedItem.selectedQuantity,
              }
            : {}),
        };

        updateDataLayer({
          event: 'addToCart',
          ecommerce: {
            currency: currencyCode,
            items: [updatedProductData],
            ...(isCommerceEnabled
              ? { value: addToCartTrayPayloadResponse?.totalBasketValue }
              : {}),
          },
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log({ error });
      setIsLoading(false);
      addToCartErrorHandler(error as AxiosError);
    }
  };

  return (
    <div
      className="sticky top-10 z-30 xl:col-span-4 xl:col-start-9 xl:row-start-2"
      data-testid={dataTestId}
    >
      <LoadingOverlay isLoading={isLoading} />

      <div className="mb-5 hidden xl:block">
        <ProductDetailsTitle
          amountSaved={amountSaved}
          dataTestId={`${dataTestId}-Product-Details-Title`}
          formattedAmountSaved={formattedAmountSaved}
          hidePriceFromSavePill={hidePriceFromSavePill}
          isRefurbished={isRefurbished}
          limitedDisplay={
            limitedDisplay ||
            status === ProductStockStatusEnum.OutOfStock ||
            status === ProductStockStatusEnum.SoldOut ||
            status === ProductStockStatusEnum.PreOrder
          }
          name={name}
          refurbishedIcon={refurbishedIcon}
          refurbishedLabel={refurbishedLabel}
          savePriceLabel={savePriceLabel}
          skuCode={skuCode}
          skuLabel={skuLabel}
        />
      </div>

      {!limitedDisplay && !!price && (
        <div className="flex flex-row justify-between xl:items-end">
          <div className="body-two flex flex-col justify-start">
            {amountSaved > 0 && formattedPrice && (
              <span
                className="text-grey-450 line-through dark:text-white"
                data-testid={`${dataTestId}-Formatted-Price`}
              >
                {formattedPrice}
              </span>
            )}
            {formattedCalculatedPrice && (
              <p
                className="h6 text-black-100 dark:text-yellow"
                data-testid={`${dataTestId}-Formatted-Calculated-Price`}
              >
                {formattedCalculatedPrice}
              </p>
            )}
          </div>

          {regionalTaxLabel && (
            <span
              className="body-four pb-1 text-grey-450 dark:text-grey-500"
              data-testid={`${dataTestId}-Tax-Label`}
            >
              {(ecoTax > 0 &&
                ecoTaxLabel &&
                ecoTaxLabel.replace('{0}', formattedEcoTax)) ||
                (copyTax > 0 &&
                  copyTaxLabel &&
                  copyTaxLabel.replace('{0}', formattedCopyTax)) ||
                regionalTaxLabel}
            </span>
          )}
        </div>
      )}
      {usbChargerLabel && usbCharger && (
        <div className="md:mt-7.5 mt-8 flex gap-2.5">
          <Icon name={`usb_${usbCharger}`} />
          <span className="body-four text-black-100 dark:text-grey-500">
            {usbChargerLabel}
          </span>
        </div>
      )}

      <div className={usbCharger ? 'mt-8 md:mt-7.5 ' : 'mt-10 xl:mt-13 '}>
        {!limitedDisplay && !!price && (
          <ProductDetailsStockStatus
            backOrderText={backOrderText}
            preOrderText={preOrderText}
            status={status}
            statusLabels={statusLabels}
            addToCartHandler={addToCartHandler}
            dataTestId={`${dataTestId}-Stock-Status`}
          />
        )}

        <div className="flex flex-col gap-13">
          <ProductDetailsOptions
            colours={colours}
            coloursLabel={coloursLabel}
            variations={variations}
            variationsLabel={variationsLabel}
            productsTitle={productsTitle}
            productsSubTitle={productsSubTitle}
            skuCode={skuCode}
            primarySkuCode={primarySkuCode}
            outOfStockLabel={statusLabels.outOfStockLabel}
            dataTestId={`${dataTestId}-Options`}
          />

          <ProductDetailsCta
            backOrderText={backOrderText}
            preOrderText={preOrderText}
            disableFindDealerCta={disableFindDealerCta}
            dealerFindLink={dealerFindLink}
            limitedDisplay={limitedDisplay}
            notifyMeContactFormEndpoint={notifyMeContactFormEndpoint}
            pricingData={pricingData}
            skuCode={skuCode}
            statusLabels={statusLabels}
            addToCartHandler={addToCartHandler}
            buyNowLabel={buyNowLabel}
            buyNowUrl={buyNowUrl}
            dataTestId={`${dataTestId}-CTA`}
          />

          {!limitedDisplay && (
            <ProductDetailsInformation
              commercialText={commercialText}
              deliveryInformation={deliveryInformation}
              refurbishedInformation={refurbishedInformation}
              shareOptions={shareOptions}
              dataTestId={`${dataTestId}-Information`}
            />
          )}

          {addToCartTrayPayload && (
            <AddToCartTray
              isOpen={isAddToCartTrayOpen}
              onSetIsOpen={setIsAddToCartTrayOpen}
              {...addToCartTrayPayload}
            />
          )}

          <InfoModal
            type={addToCartModalContent.type}
            isModalOpen={isAddToCartModalOpen}
            toggleModal={setIsAddToCartModalOpen}
            title={addToCartModalContent.title}
            description={addToCartModalContent.description}
            ctaLink={addToCartModalContent.ctaLink}
            shouldLockBody={!isAddToCartTrayOpen}
            dataTestId={`${dataTestId}-Add-To-Cart-Modal`}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsSidePanel;
